import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type {
  FilterState,
  HomeRoomsAndBeds,
  PriceRange,
} from "@b2bportal/core-types";
import type {
  Amenity,
  HotelStarRating,
  LodgingHomesAmenityKind,
  MealPlanKind,
  StayType,
} from "@b2bportal/lodging-api";
import {
  HotelsAvailabilityActions,
  HotelsAvailabilitySelectors,
} from "../store";

export const useHotelsAvailabilityFilters = () => {
  const dispatch = useDispatch();
  const filters = useSelector(HotelsAvailabilitySelectors.getFilters);

  const hasFilters = useSelector(
    HotelsAvailabilitySelectors.hasSetFiltersSelector
  );

  const totalFilterCount = useSelector(
    HotelsAvailabilitySelectors.amountOfSelectedFilters
  );

  const hasSetPriceFilterSelector = useSelector(
    HotelsAvailabilitySelectors.hasSetPriceFilterSelector
  );

  const filterOptions = useSelector(
    HotelsAvailabilitySelectors.getFilterOptions
  );

  const setPriceFilter = useCallback(
    (val: PriceRange) =>
      dispatch(HotelsAvailabilityActions.setPriceFilter(val)),
    [dispatch]
  );

  const setStarRatingFilter = useCallback(
    (val: HotelStarRating[]) =>
      dispatch(HotelsAvailabilityActions.setStarRatingFilter(val)),
    [dispatch]
  );

  const setCancellationFilter = useCallback(
    (val: boolean) =>
      dispatch(HotelsAvailabilityActions.setCancellationFilter(val)),
    [dispatch]
  );

  const setAmenitiesFilter = useCallback(
    (val: Amenity[]) => {
      dispatch(HotelsAvailabilityActions.setAmenitiesFilter(val));
    },
    [dispatch]
  );

  const setPayLaterFilter = useCallback(
    (val: boolean) => {
      dispatch(HotelsAvailabilityActions.setPayLaterFilter(val));
    },
    [dispatch]
  );

  const setHotelNameFilter = useCallback(
    (val: string) => {
      dispatch(HotelsAvailabilityActions.setHotelNameFilter(val));
    },
    [dispatch]
  );

  const setMealPlanFilter = useCallback(
    (val: MealPlanKind[]) => {
      dispatch(HotelsAvailabilityActions.setMealPlanFilter(val));
    },
    [dispatch]
  );

  const setStayTypeFilter = useCallback(
    (val: StayType[]) => {
      dispatch(HotelsAvailabilityActions.setStayTypeFilter(val));
    },
    [dispatch]
  );

  const setHomeAmenitiesFilter = useCallback(
    (val: LodgingHomesAmenityKind[]) => {
      dispatch(HotelsAvailabilityActions.setHomeAmenitiesFilter(val));
    },
    [dispatch]
  );

  const setHomeRoomsAndBedsFilter = useCallback(
    (val?: HomeRoomsAndBeds) => {
      dispatch(HotelsAvailabilityActions.setHomeRoomsAndBedsFilter(val));
    },
    [dispatch]
  );

  const setUserRatingFilter = useCallback(
    (val: number) => {
      dispatch(HotelsAvailabilityActions.setUserRatingFilter(val));
    },
    [dispatch]
  );

  const resetUserRatingFilter = useCallback(
    () => dispatch(HotelsAvailabilityActions.resetUserRatingFilter()),
    [dispatch]
  );

  const resetFilters = useCallback(
    () => dispatch(HotelsAvailabilityActions.resetFilters()),
    [dispatch]
  );

  const resetStarRatingFilter = useCallback(
    () => dispatch(HotelsAvailabilityActions.resetStarRatingFilter()),
    [dispatch]
  );

  const resetCancellationFilter = useCallback(
    () => dispatch(HotelsAvailabilityActions.resetCancellationFilter()),
    [dispatch]
  );

  const resetAmenitiesFilter = useCallback(
    () => dispatch(HotelsAvailabilityActions.resetAmenitiesFilter()),
    [dispatch]
  );

  const setAllFilters = useCallback(
    (localFilters: FilterState) => {
      dispatch(HotelsAvailabilityActions.setAllFilters(localFilters));
    },
    [dispatch]
  );

  const context = useMemo(
    () => ({
      ...filters,
      hasSetPriceFilterSelector,
      hasFilters,
      filterOptions,
      price: filters.priceRange,
      hadSetFilters: hasFilters,
      totalFilterCount,
      userRating: filters.userRating ?? 0,
    }),
    [
      filters,
      hasSetPriceFilterSelector,
      hasFilters,
      filterOptions,
      totalFilterCount,
    ]
  );

  const handlers = useMemo(
    () => ({
      setPriceFilter,
      setStarRatingFilter,
      setCancellationFilter,
      setAmenitiesFilter,
      setPayLaterFilter,
      setHotelNameFilter,
      setMealPlanFilter,
      setStayTypeFilter,
      setHomeAmenitiesFilter,
      setHomeRoomsAndBedsFilter,
      setUserRatingFilter,
      resetUserRatingFilter,
      resetFilters,
      resetStarRatingFilter,
      resetCancellationFilter,
      resetAmenitiesFilter,
      setAllFilters,
    }),
    [
      setPriceFilter,
      setStarRatingFilter,
      setCancellationFilter,
      setAmenitiesFilter,
      setPayLaterFilter,
      setHotelNameFilter,
      setMealPlanFilter,
      setStayTypeFilter,
      setHomeAmenitiesFilter,
      setHomeRoomsAndBedsFilter,
      setUserRatingFilter,
      resetUserRatingFilter,
      resetFilters,
      resetStarRatingFilter,
      resetCancellationFilter,
      resetAmenitiesFilter,
      setAllFilters,
    ]
  );

  return {
    context,
    handlers,
  };
};
